import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import type { Theme } from '@mui/material/styles'
import type { JSX } from 'react'

import { Heading2, Heading3 } from '../../data-display/typography/Typography'
import { useScreenSize } from '../../hooks/useScreenSize'
import { IconButton } from '../../inputs/icon-button'

export interface TopBarProps {
  leftButton?: TopBarButtonProps
  rightButton?: TopBarButtonProps | JSX.Element
  title?: string
  description?: JSX.Element
  hasHubloLogo?: boolean
  position?: 'fixed' | 'absolute' | 'sticky' | 'static' | 'relative'
  backgroundColor?: keyof Theme['palette']['background']
  /** Thin line under the bar (e.g. to separate from scrolling content). */
  showBottomDivider?: boolean
}

export interface TopBarButtonProps {
  disabled?: boolean
  icon: JSX.Element
  onClick?: () => void
  'data-testid'?: string
  ariaLabel?: string
}

export const TopBar = ({
  leftButton,
  rightButton,
  title = '',
  description,
  position = 'static',
  backgroundColor = 'default',
  showBottomDivider = false,
}: TopBarProps) => {
  const { isSmallScreen } = useScreenSize()
  const hasOnlyRightButton = !leftButton && rightButton && !title
  const hasNoTitleWithButtons =
    (leftButton && rightButton && !title) ||
    (title && rightButton && !leftButton)

  const isTopBarButtonProps = (
    button: TopBarButtonProps | JSX.Element,
  ): button is TopBarButtonProps => {
    return 'icon' in button
  }

  return (
    <AppBar
      position={position}
      role="banner"
      sx={(theme) => {
        const resolvedBackgroundColor =
          theme.palette.background[backgroundColor]

        return {
          backgroundColor: resolvedBackgroundColor,
          color: theme.palette.getContrastText(resolvedBackgroundColor),
          boxShadow: 'none',
          ...(showBottomDivider && {
            borderBottom: `1px solid ${theme.palette.border.subtle}`,
          }),
          '& .MuiIconButton-root': {
            color: 'inherit',
          },
        }
      }}
    >
      <Toolbar
        sx={{
          gap: 2,
          px: { sm: 2 },
          justifyContent: hasOnlyRightButton
            ? 'flex-end'
            : hasNoTitleWithButtons
              ? 'space-between'
              : 'flex-start',
          '@media (max-width:600px) and (orientation: landscape)': {
            minHeight: '56px',
          },
          '& .MuiTypography-root': {
            flex: 1,
          },
          '& > .MuiIconButton-root:last-child': {
            ml: 'auto',
          },
          '& > :last-child': rightButton ? { ml: 'auto' } : undefined,
        }}
      >
        {leftButton && (
          <IconButton
            disabled={leftButton.disabled}
            edge="start"
            onClick={leftButton.onClick}
            data-testid={leftButton['data-testid']}
            ariaLabel={leftButton.ariaLabel}
          >
            {leftButton.icon}
          </IconButton>
        )}
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            pt: description ? 4 : 0,
            '& p': {
              color: 'inherit',
            },
          }}
        >
          {isSmallScreen ? (
            <Heading3 component="div">{title}</Heading3>
          ) : (
            <Heading2 component="div">{title}</Heading2>
          )}
          {description}
        </Box>
        {rightButton &&
          (isTopBarButtonProps(rightButton) ? (
            <IconButton
              disabled={rightButton.disabled}
              edge="end"
              onClick={rightButton.onClick}
              data-testid={rightButton['data-testid']}
              ariaLabel={rightButton.ariaLabel}
            >
              {rightButton.icon}
            </IconButton>
          ) : (
            rightButton
          ))}
      </Toolbar>
    </AppBar>
  )
}
